import useEventCallback from 'hooks/useEventCallback';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearErrorForm,
  setFieldErrorForm,
  setFieldsErrorForm,
  setNewErrorForm,
} from 'store/reducers/form';

export const ValidateContext = createContext({
  registerField: (key, validate) => null,
  onChangeField: (key, value) => null,
  unRegisterField: (key) => null,
  onDependChange: (key, value) => null,
  validateField: (key) => null,
});

export const useGetError = (key) => {
  const { formName } = useContext(ValidateContext);
  return useSelector(
    (state) => state.form?.[formName]?.[key],
    (pre, next) => pre === next
  );
};

export const useValidate = (formName) => {
  const values = useRef({});
  const oldValues = useRef({});
  const fields = useRef({});
  const depend = useRef({});
  const validateCallbacksOnChange = useRef({});
  const dispatch = useDispatch();
  const errors = useSelector((state) => state.form?.[formName]);

  const onDependChange = (key, value) => {
    depend.current[key] = value;
  };

  const registerField = (key, validate, validateCallback) => {
    fields.current[key] = validate;
    validateCallbacksOnChange.current[key] = validateCallback;
  };

  const setErrors = useEventCallback(
    (callback) => {
      const newError = callback(errors);
      dispatch(setNewErrorForm({ formName, errors: newError }));
    },
    [errors]
  );

  const validateField = useEventCallback(
    (key) => {
      const value = values.current[key];
      const validate = fields.current[key];
      const oldValue = oldValues.current[key];
      oldValues.current[key] = null;

      const validateCallback = validateCallbacksOnChange.current[key];
      if (validateCallback) {
        const errorNews = validateCallback(
          { values, fields, oldValue, key, depend },
          setErrors
        );
        dispatch(setFieldsErrorForm({ formName, errors: errorNews }));
      }

      if (validate) {
        const newE = validate(value, values.current, key, depend.current);
        dispatch(setFieldErrorForm({ formName, name: key, error: newE }));
        return newE;
      }
    },
    [setErrors]
  );

  const onChangeValue = (key, value) => {
    if (!oldValues.current[key]) {
      oldValues.current[key] = values.current[key];
    }
    values.current[key] = value;
  };

  const validateFields = useCallback(() => {
    const keys = Object.keys(fields.current);
    const err = keys
      .map((k) => {
        const v = values.current[k];
        const validate = fields.current[k];

        let error;
        if (validate) {
          error = validate(v, values.current, k, depend.current);
        }

        return error ? [k, error] : null;
      })
      .filter(Boolean);

    dispatch(setNewErrorForm({ errors: Object.fromEntries(err), formName }));
    return err?.length > 0 ? Object.fromEntries(err) : null;
    // eslint-disable-next-line
  }, []);

  const unRegisterField = (key) => {
    delete fields.current[key];
    delete values.current[key];
    delete depend.current[key];
    delete oldValues.current[key];

    dispatch(setFieldErrorForm({ formName, name: key, error: null }));
  };

  const resetValidate = () => {
    setTimeout(() => dispatch(clearErrorForm({ formName })), 100);
  };

  useEffect(() => {
    return () => setTimeout(() => dispatch(clearErrorForm({ formName })), 100);
    // eslint-disable-next-line
  }, []);

  return useMemo(
    () => ({
      formName,
      registerField: registerField,
      onChangeField: onChangeValue,
      validateFields,
      validateField,
      unRegisterField,
      resetValidate,
      onDependChange,
    }),
    // eslint-disable-next-line
    []
  );
};
